import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { useToast } from '@aimsos/ui';

import { requestFactChange } from '../../../services/truth.js';

/**
 * "Request change" on a Truth fact (CT-5719). Truth is not edited here: the
 * request opens in Helix Governance Studio, and the fact stays as it is until it
 * is approved there. A toast says how it went. One request per fact at a time
 * (`submittingRef` holds the ids in flight), so a double click asks once.
 */
export function useRequestFactChange({ entityType, entityId } = {}) {
  const { t } = useTranslation('unified-profiles');
  const toast = useToast();
  const [requestingIds, setRequestingIds] = useState(() => new Set());
  const submittingRef = useRef(new Set());

  const setRequesting = useCallback((factId, requesting) => {
    setRequestingIds((current) => {
      const next = new Set(current);
      if (requesting) next.add(factId);
      else next.delete(factId);
      return next;
    });
  }, []);

  const requestChange = useCallback(
    async (fact) => {
      if (!fact?.id || submittingRef.current.has(fact.id)) return false;
      submittingRef.current.add(fact.id);
      setRequesting(fact.id, true);
      try {
        await requestFactChange({ entityType, entityId, factId: fact.id });
        toast.success({ title: t('knowledge.truth.toast.changeTitle'), message: fact.label });
        return true;
      } catch (err) {
        toast.error({
          title: t('knowledge.truth.toast.changeErrorTitle'),
          message: err?.message ?? t('knowledge.truth.toast.errorMessage'),
        });
        return false;
      } finally {
        submittingRef.current.delete(fact.id);
        setRequesting(fact.id, false);
      }
    },
    [entityType, entityId, setRequesting, toast, t],
  );

  return useMemo(() => ({ requestChange, requestingIds }), [requestChange, requestingIds]);
}

export default useRequestFactChange;
